import { motion } from 'framer-motion';
import { 
  HouseLine, 
  ShieldCheck, 
  CurrencyEur, 
  Users, 
  CalendarCheck, 
  TrendUp 
} from '@phosphor-icons/react';

const usps = [
  {
    icon: HouseLine,
    title: 'Alles aus einer Hand',
    description: 'Komplette Projektleitung von der Planung bis zur schlüsselfertigen Übergabe.'
  },
  {
    icon: ShieldCheck,
    title: 'Hohe Planungssicherheit',
    description: 'Professionelle Planung und Durchführung durch unser erfahrenes Team.'
  },
  {
    icon: CurrencyEur,
    title: 'Kostenkontrolle durch Festpreis',
    description: 'Transparente Preisgestaltung ohne versteckte Kosten oder Überraschungen.'
  },
  {
    icon: Users,
    title: 'Ein Experte als Ansprechpartner',
    description: 'Persönliche Betreuung während des gesamten Sanierungsprozesses.'
  },
  {
    icon: CalendarCheck,
    title: 'Termingerechte Fertigstellung',
    description: 'Verbindliche Zeitplanung und pünktliche Übergabe Ihres Projekts.'
  },
  {
    icon: TrendUp,
    title: 'Wertsteigernde Maßnahmen',
    description: 'Sanierungen, die den Wert Ihrer Immobilie nachhaltig steigern.'
  }
];

const USPSection = () => {
  return (
    <section className="py-24 md:py-32 bg-dark-secondary" data-testid="usp-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-gold font-sans text-sm uppercase tracking-[0.2em] mb-4 block">
            Ihre Vorteile
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
            So leicht war Sanieren noch nie
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-base sm:text-lg">
            Mit den Wohntraum-Experten sparen Sie Zeit, Nerven und minimieren das Risiko 
            von Mehrkosten und Bauverzögerungen.
          </p>
        </motion.div>

        {/* USP Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {usps.map((usp, index) => (
            <motion.div
              key={usp.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="glass-card p-6 md:p-8 transition-all duration-300 group"
              data-testid={`usp-card-${index}`}
            >
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-5 group-hover:bg-gold/20 transition-colors">
                <usp.icon size={24} className="text-gold" weight="light" />
              </div>
              <h3 className="font-serif text-xl text-white mb-3">
                {usp.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                {usp.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Warning Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 bg-dark-tertiary/50 border border-white/5 p-6 md:p-8"
        >
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="w-16 h-16 bg-red-500/10 flex items-center justify-center shrink-0">
              <ShieldCheck size={32} className="text-red-400" weight="light" />
            </div>
            <div>
              <h3 className="font-serif text-xl text-white mb-3">
                Gefahren bei Sanierung in Eigenregie
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-4">
                Eine Sanierung in Eigenregie bedeutet hohen Aufwand, erhöhte Kostenrisiken 
                und unklare Zuständigkeiten. Ohne zentrale Koordination steigt das Risiko 
                von Bauverzögerungen und Qualitätsproblemen erheblich.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-white/50 text-sm">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                  Hoher Organisationsaufwand
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                  Kommunikationsprobleme
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                  Unklare Kostenkontrolle
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                  Versteckte Zusatzkosten
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default USPSection;
